// © 2026 Holmes Computer Consultants. All rights reserved. -- www.holmesconsultants.com
//
// Getting the browser out of the way of the machine (M3-45).
//
// THE MEASUREMENT THAT MADE THIS A DEFECT
// ---------------------------------------
// On a phone in **landscape** — 844 × 390 — the shell's top bar takes 56 px and the bottom
// navigation takes 64. That is **120 px of chrome out of 390: thirty-one per cent of the screen**,
// leaving a playfield 270 px tall. Landscape is the posture where a two-handed grip puts a thumb
// on each flipper zone, which is the best way to play this game, and it was being given the
// smallest machine in the product. Portrait loses the same 120 px out of 844, where it barely
// registers.
//
// WHAT FOCUS MODE IS
// ------------------
// The shell's own rows collapse — the top bar and the navigation are **removed from the tree**,
// not hidden, for the same reason `AppShell` already does not render a bottom bar on a desktop: a
// hidden nav is still in the accessibility tree, so a keyboard player tabs through destinations
// that are not on screen.
//
// **Fullscreen is asked for as well, and separately.** They are two different wins and neither
// implies the other: collapsing the rows recovers this product's chrome, and `requestFullscreen`
// recovers the *browser's* — the address bar and the system navigation, which on Android is
// another 100-odd pixels this application cannot otherwise touch. Fullscreen can be refused (iOS
// Safari on iPhone has never implemented it for arbitrary elements) and focus mode still works
// there, because the rows are ours to collapse whatever the browser allows.
//
// WHY A SIGNAL RATHER THAN A ROUTER STATE
// ---------------------------------------
// `MachineStage` decides — it is the thing that knows a ball is in play — and `AppShell` renders,
// and neither is the other's parent. The same shape `entitlementSignal.ts` and
// `useAccount.setImmersive` already use, and for the same reason: one fact, needed on both sides
// of a boundary, with no component tree connecting them.
//
// **It lives outside the account subtree.** The front door is the busiest place this is wanted and
// FR-030 forbids anything there that could reach a network or construct a Supabase client, so this
// module imports nothing at all.

type Listener = (on: boolean) => void

let focused = false
const listeners = new Set<Listener>()

/** Is the shell currently out of the way? */
export function isPlayFocused(): boolean {
  return focused
}

/**
 * Turn focus mode on or off.
 *
 * Idempotent, and deliberately silent when nothing changed: the stage calls this from an effect
 * that re-runs whenever a readout changes, which is every frame a score moves.
 */
export function setPlayFocus(on: boolean): void {
  if (focused === on) return
  focused = on
  for (const listener of listeners) listener(on)
}

/** Watch it. Returns the unsubscribe. */
export function watchPlayFocus(listener: Listener): () => void {
  listeners.add(listener)
  return () => {
    listeners.delete(listener)
  }
}

/** Everything this needs of an element to make it fullscreen. A real one satisfies it. */
export interface FullscreenTarget {
  requestFullscreen?: () => Promise<void>
  /** Safari on iPad and older WebKit. Present as a callback API rather than a promise. */
  webkitRequestFullscreen?: () => void
}

export interface FullscreenDocument {
  readonly fullscreenElement?: unknown
  readonly webkitFullscreenElement?: unknown
  exitFullscreen?: () => Promise<void>
  webkitExitFullscreen?: () => void
}

/** Is anything fullscreen right now? */
export function isFullscreen(doc: FullscreenDocument | undefined = browserDocument()): boolean {
  if (doc === undefined) return false
  return (doc.fullscreenElement ?? doc.webkitFullscreenElement) != null
}

/**
 * Ask for fullscreen, and never mind if the answer is no.
 *
 * **Silent on refusal, like the wake lock and for the same reason.** iOS Safari on iPhone has no
 * element fullscreen at all; a browser can refuse when the call is not inside a user gesture; an
 * embedded webview can have it disabled by policy. None of those is a fault a player could act on,
 * and focus mode has already done the half of the job that is ours.
 */
export async function requestFullscreen(
  target: FullscreenTarget | undefined,
): Promise<void> {
  if (target === undefined) return
  try {
    if (typeof target.requestFullscreen === 'function') {
      await target.requestFullscreen()
      return
    }
    target.webkitRequestFullscreen?.()
  } catch {
    // Refused. See above.
  }
}

/** Give it back. Also silent, and also safe to call when nothing is fullscreen. */
export async function exitFullscreen(
  doc: FullscreenDocument | undefined = browserDocument(),
): Promise<void> {
  if (doc === undefined || !isFullscreen(doc)) return
  try {
    if (typeof doc.exitFullscreen === 'function') {
      await doc.exitFullscreen()
      return
    }
    doc.webkitExitFullscreen?.()
  } catch {
    // As above.
  }
}

function browserDocument(): FullscreenDocument | undefined {
  return (globalThis as { document?: FullscreenDocument }).document
}

/**
 * What a pulled plunger should do to the screen (M3-45, M8-73).
 *
 * **A decision, separated from the two side effects it causes**, so the rule can be read and
 * tested without a browser, a canvas or a gesture.
 *
 * **Every pull takes the screen, and every pull asks for fullscreen.** Decided 2 Sep 2026, after
 * a phone reported the bars still standing over a game: pulling the plunger is the player saying
 * they are playing now, and that is true of every ball. This supersedes two M3-45 rules — that a
 * stored "off" stopped the rows collapsing, and that fullscreen was asked for once a visit. Both
 * were written so as not to overrule a player, and both left a player with no way back in:
 * somebody who pressed "Leave focus mode" once, or left fullscreen with a system gesture once,
 * never got the larger machine from the plunger again, and nothing said why. The stored choice
 * still decides what a player *arrives* to — see `focusStartsOn` — and the menu row still gives
 * the bars back mid-game.
 *
 * Idempotent in both halves: already focused is not a reason to do anything, and a browser that
 * is already fullscreen is not asked again. A browser with no element fullscreen — Safari on an
 * iPhone — refuses silently every time, and the rows collapse regardless.
 */
export interface PlungeScreen {
  /** Turn focus mode on. False only when it already is. */
  readonly focus: boolean
  /** Ask the browser for fullscreen. False only when it already is. */
  readonly fullscreen: boolean
}

export function screenOnPlunge(now: {
  readonly focused: boolean
  readonly fullscreen: boolean
}): PlungeScreen {
  return { focus: !now.focused, fullscreen: !now.fullscreen }
}
